import React, { useState } from 'react';
import { Upload, X, File, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CodeUploadProps {
    onFilesChange: (files: any[]) => void;
    acceptedTypes: string[];
}

export function CodeUpload({ onFilesChange, acceptedTypes }: CodeUploadProps) {
    const [files, setFiles] = useState<any[]>([]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        const validFiles = selectedFiles.filter(file => {
            const ext = '.' + file.name.split('.').pop()?.toLowerCase();
            return acceptedTypes.includes(ext);
        });

        const newFiles = validFiles.map(file => ({
            name: file.name,
            type: file.name.split('.').pop()?.toLowerCase() === 'doc' ? 'documentation' : 'source',
            file: file,
            size: file.size
        }));

        const updatedFiles = [...files, ...newFiles];
        setFiles(updatedFiles);
        onFilesChange(updatedFiles);
    };

    const removeFile = (index: number) => {
        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        onFilesChange(updatedFiles);
    };

    return (
        <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors bg-muted/5">
                <input
                    type="file"
                    multiple
                    accept={acceptedTypes.join(',')}
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                    <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium text-foreground mb-2">
                        Upload Algorithm Files
                    </p>
                    <p className="text-sm text-muted-foreground">
                        Drag and drop or click to upload ({acceptedTypes.join(', ')})
                    </p>
                </label>
            </div>

            {files.length > 0 && (
                <div className="space-y-2">
                    {files.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-card border rounded-lg group">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-muted rounded">
                                    <File className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <div className="text-sm font-medium">{file.name}</div>
                                    <div className="text-xs text-muted-foreground lowercase">
                                        {(file.size / 1024).toFixed(1)} KB • {file.type}
                                    </div>
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeFile(index)}
                                className="text-muted-foreground hover:text-destructive"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
